/** A paragraph, or a line set apart for emphasis */
export type Para = string | { emphasis: string };

export type DocImage = {
  label: string;
  caption: string;
  /** Full-length image; shown cropped from the top, opens at full height */
  image?: string;
};

export type ProcessStep = {
  title: string;
  body: string;
  /** Guiding questions asked during this step */
  questions?: string[];
  /** Worked example shown in a dusty blue box */
  example?: string;
};

export type KeyDecision = {
  title: string;
  /** Opening line set slightly larger */
  lede?: string;
  body: Para[];
  /** Closing statement set apart */
  closing?: string;
};

export type BeforeAfterPanel = {
  label: "Before" | "After";
  headline: string;
  body: string[];
  image?: string;
};

export type CaseStudyContent = {
  /** Position in the Selected Recent Work sequence; omitted for in-progress work */
  number?: string;
  lede: string;
  facts: { label: string; value: string }[];
  challenge: { headline: string; body: Para[] };
  solution: { headline: string; body: Para[] };
  results: {
    heading: string;
    /** "metrics" = measured results; "shifts" = from → to changes */
    kind: "metrics" | "shifts";
    items: { value: string; label?: string; detail?: string }[];
    note?: string;
  };
  process: {
    /** "grid" = two-column steps (default); "timeline" = connected vertical sequence */
    layout?: "grid" | "timeline";
    /** Optional name for the model, shown above the steps */
    title?: string;
    steps: ProcessStep[];
    flow?: string[];
    closing?: string;
  };
  /** Standout features of what was built, with one lead feature */
  features?: {
    heading: string;
    lead: { eyebrow: string; title: string; body: string[]; visual?: "chat" | "exchange" };
    items: { title: string; body: string }[];
  };
  decisions: KeyDecision[];
  beforeAfter?: BeforeAfterPanel[];
  learned: { headline: string; body: string[]; closing?: string };
  deeper?: { title: string; body: string[]; cta: string };
  /** Slugs from data/thoughts.ts */
  relatedThoughts: string[];
};

export const caseStudies: Record<string, CaseStudyContent> = {
  "lawrence-website": {
    number: "02",
    lede: "Transforming a 10,000+ page web ecosystem into a focused public product built to learn and evolve.",
    facts: [
      { label: "Role", value: "Director of Digital Strategy & Web Product" },
      { label: "Scope", value: "Digital Transformation · Web Product · UX · Organic Discovery" },
    ],
    challenge: {
      headline: "The problem wasn’t just an outdated website. It was an outdated operating model for the web.",
      body: [
        "Years of accumulated content, technology and internal requests had created a sprawling ecosystem. The challenge was bigger than redesigning Lawrence.edu. We needed to decide what belonged on the public website, whom it needed to serve and how decisions about it would be made after launch.",
        "At the same time, the website increasingly needed to work for two audiences: the people making consequential decisions about Lawrence and the search and AI systems helping them find information.",
      ],
    },
    solution: {
      headline: "Make launch the midpoint.",
      body: [
        "I led the transition to a deliberately focused LaunchPad and a Growth-Driven Design model where we didn’t try to solve every legacy problem before going live.",
        "Instead, we established the right foundation, launched a much smaller public product and moved immediately into continuous improvement driven by user behavior, institutional priorities and measurable outcomes.",
      ],
    },
    results: {
      heading: "Early Results",
      kind: "metrics",
      items: [
        {
          value: "10,000+ → ~200",
          label: "Public-facing pages",
          detail: "A deliberately focused public experience launched July 28–29, 2026.",
        },
        {
          value: "+15% / −12%",
          label: "Engaged sessions / bounce rate",
          detail: "Majors & Programs · Aug.–Sept. 2026 vs. Aug.–Sept. 2025",
        },
        {
          value: "~67% ↑",
          label: "AI citations",
          detail: "Approximately 3,000 → 5,000 YoY · As of Oct. 2, 2026",
        },
      ],
      note: "Early post-launch results. Lawrence.edu launched July 28–29, 2026. Performance data current as of Oct. 2, 2026.",
    },
    process: {
      steps: [
        {
          title: "Observe",
          body: "Use analytics, heatmaps, search behavior, user questions and institutional data to understand what people are actually doing.",
        },
        {
          title: "Prioritize",
          body: "Balance user impact, institutional goals, urgency and available capacity instead of treating every request as equally important.",
        },
        {
          title: "Improve",
          body: "Turn evidence into a defined release completed through a focused sprint.",
        },
        {
          title: "Measure",
          body: "Use the result, or the new baseline it creates, to determine what comes next.",
        },
      ],
    },
    features: {
      heading: "What We Built",
      lead: {
        eyebrow: "AI-powered chatbot",
        visual: "chat",
        title: "Answers in the moment.",
        body: [
          "An AI-powered chatbot helps prospective students, families and other visitors get answers when they need them, instead of hunting through pages.",
          "Its questions also become another signal for what people need next, feeding into how we prioritize future improvements.",
        ],
      },
      items: [
        {
          title: "Update Once, Show Up Everywhere",
          body: "Reusable, bottom-line-focused components promote the actions that matter most to Lawrence. They’re managed centrally, so one update appears everywhere they’re used at the same time.",
        },
        {
          title: "Purposeful Movement",
          body: "Motion brings the experience to life and draws attention to what matters, without getting in the way of finding information.",
        },
        {
          title: "One Academic Discovery Experience",
          body: "A single Areas of Study framework brings the College and Conservatory together into one path for exploring programs.",
        },
        {
          title: "Built for Search and AI Discovery",
          body: "Structured, predictable content patterns help search engines and AI systems understand and represent Lawrence’s information.",
        },
      ],
    },
    decisions: [
      {
        title: "Launch Smaller on Purpose",
        lede: "The goal wasn’t to recreate 10,000+ pages in a new CMS.",
        body: [
          "We separated the job of the public website from the jobs of internal systems and legacy content, then concentrated the LaunchPad around priority audiences, decisions and actions.",
        ],
        closing: "A smaller launch wasn’t a compromise. It was the product strategy.",
      },
      {
        title: "One University. One Academic Discovery Experience.",
        body: [
          "Lawrence’s College and Conservatory had historically created separate paths into academic exploration. But that fragmentation didn’t reflect Lawrence’s “one university” identity or the way students actually explore programs.",
          "We created one Areas of Study framework, then used post-launch evidence to refine classifications, taxonomy, terminology and pathways into program pages.",
        ],
      },
      {
        title: "Let Behavior Challenge Convention",
        lede: "I don’t believe “best practice” should end a product conversation.",
        body: [
          "Post-launch analytics, heatmaps, search behavior and user questions became inputs into product decisions. When behavior exposed friction, we could prioritize around what users were actually doing rather than what we assumed they would do.",
        ],
        closing: "Best practices informed the decision. Our users made the decision.",
      },
    ],
    beforeAfter: [
      {
        label: "Before",
        headline: "A website shaped by years of accumulation.",
        image:
          "https://cdn.magicpatterns.com/uploads/ecgkwUPfj9tKvEG6F8ugg6/Liberal_Arts_College___Appleton,_WI_(3).png",
        body: [
          "The legacy experience reflected years of content growth, decentralized publishing and evolving institutional needs. The scale made it difficult to maintain clear pathways, consistent information and a focused public experience.",
        ],
      },
      {
        label: "After",
        headline: "A focused foundation built to evolve.",
        image:
          "https://cdn.magicpatterns.com/uploads/tfBoSGspjK7AuoDdhL8x8g/Liberal_Arts_College___Appleton,_WI_(4).png",
        body: [
          "The LaunchPad introduced a modern design with purposeful movement and a more intentional structure built around priority audiences, clearer decisions and human, search and AI discovery.",
          "Centrally managed, bottom-line-focused components can be updated across the site simultaneously, and an AI-powered chatbot helps visitors find answers in the moment.",
          "Just as importantly, it established a foundation that could change as the evidence changed.",
        ],
      },
    ],
    learned: {
      headline: "A website transformation doesn’t end when the new website launches.",
      body: [
        "In some ways, that’s when the most useful work begins. Launch gives you something strategy documents, stakeholder conversations and prototypes never can: real people using the real product.",
        "The goal is to build an organization capable of learning from that behavior and changing with it.",
      ],
    },
    deeper: {
      title: "Want to Go Deeper?",
      body: [
        "The visual transformation is only part of this story.",
        "I can share more about the research, information architecture, product strategy, stakeholder and vendor leadership, prioritization process and post-launch optimization in conversation.",
      ],
      cta: "Discuss this project",
    },
    relatedThoughts: ["progress-should-still-feel-human"],
  },

  "lawrence-search-social": {
    lede: "Building a more connected digital experience by aligning what audiences search for, what they see on social media, and how they experience a university online.",
    facts: [
      { label: "Role", value: "Organic Content & Social Media Strategy" },
      { label: "Focus", value: "SEO · Social Media · Content Strategy · Digital Experience" },
    ],
    challenge: {
      headline: "Different channels. One audience. Too many disconnected experiences.",
      body: [
        "Prospective students encounter a university through search results, social media, website content, and countless small digital interactions.",
        "Each channel has a different purpose, but the experience should feel connected.",
        "The challenge wasn’t simply creating more content. It was establishing a clearer relationship between information, storytelling, and audience needs.",
        { emphasis: "How do you make every channel useful on its own while ensuring they work better together?" },
      ],
    },
    solution: {
      headline: "Give every channel a purpose. Build the connections between them.",
      body: [
        "Developed an integrated organic content and social media framework connecting search strategy, audience-centered storytelling, and the website experience.",
        "Rather than treating each channel as a separate publishing destination, the approach considered how people discover information, develop interest, and move between digital touchpoints.",
        { emphasis: "The goal: Make content easier to find, more meaningful to encounter, and more connected across the experience." },
      ],
    },
    results: {
      heading: "What Changed",
      kind: "shifts",
      items: [
        {
          value: "Search → Answers",
          detail: "Prioritized audience questions and search intent to guide content development and improve discoverability.",
        },
        {
          value: "Social → Connection",
          detail: "Clarified complementary roles for social channels, balancing practical information with authentic institutional storytelling.",
        },
        {
          value: "Website → Experience",
          detail: "Connected homepage imagery and messaging to the broader audience journey.",
        },
        {
          value: "Strategy → Alignment",
          detail: "Established a shared framework for more intentional planning and collaboration across digital channels.",
        },
      ],
    },
    process: {
      steps: [
        {
          title: "Understand",
          body: "Examined audience needs, existing content opportunities, and the distinct roles of digital channels.",
        },
        {
          title: "Connect",
          body: "Identified where search, social media, and website experiences could reinforce one another without duplicating content.",
        },
        {
          title: "Strategize",
          body: "Developed an audience-centered framework to guide content priorities, channel responsibilities, and storytelling.",
        },
        {
          title: "Align",
          body: "Connected content recommendations with visual direction and opportunities for cross-functional collaboration.",
        },
        {
          title: "Measure",
          body: "Established recommended signals for evaluating visibility, engagement, and meaningful audience actions.",
        },
      ],
    },
    decisions: [
      {
        title: "Don’t Make Every Channel Do Everything",
        lede: "A strong digital presence doesn’t require identical messages everywhere.",
        body: [
          "Different platforms serve different audience needs. The strategy gave each channel a distinct role while maintaining a cohesive institutional voice.",
        ],
      },
      {
        title: "Lead With What People Need",
        lede: "Content works harder when it answers a real question.",
        body: [
          "An audience-first approach to search and storytelling helped connect useful information with more engaging digital experiences.",
        ],
      },
      {
        title: "Treat the Website and Social as One Experience",
        lede: "A university’s digital identity isn’t defined by a single platform.",
        body: [
          "Connecting what audiences discover through search, encounter on social, and experience on the website creates a more consistent and recognizable brand.",
        ],
      },
    ],
    learned: {
      headline: "Integration isn’t about making everything the same.",
      body: [
        "It’s about understanding what each channel does best and designing the relationships between them.",
        "The strongest content strategies create clarity without sacrificing creativity—and give individual teams room to do meaningful work within a shared direction.",
      ],
      closing: "Better connections make better experiences.",
    },
    relatedThoughts: [],
  },

  "wooster-enrollment-journey": {
    number: "03",
    lede: "Grounded in a large brand messaging update, designing one prospective-student journey across organic and paid digital media, Inbound Marketing and a HubSpot nurture campaign that moved students toward application based on the actions they took.",
    facts: [
      { label: "Role", value: "Director of Digital Marketing" },
      {
        label: "Scope",
        value: "Brand Messaging · Customer Journey · Organic & Paid Media · Inbound Marketing · HubSpot · Marketing Automation",
      },
    ],
    challenge: {
      headline: "We had technology. We didn’t have a connected journey.",
      body: [
        "HubSpot existed. Slate existed. Websites, advertising and campaigns existed. But they hadn’t been intentionally designed around the experience of one prospective student moving between them.",
        "Analytics across digital properties weren’t reliably connected. Campaigns were often reactive. Landing experiences were generic.",
        "We could generate traffic.",
        "The more interesting question was:",
        { emphasis: "What happened to a person after they arrived?" },
      ],
    },
    solution: {
      headline: "Design the journey before redesigning the website.",
      body: [
        "Before tackling a larger website transformation, I focused on the system underneath it.",
        "It started with what we were saying. Wooster was in the middle of a large brand messaging update, and I served on the committee that shaped it. That gave the journey a clear, consistent story to carry before a single campaign was built.",
        "With the messaging in place, I built three connected pieces: a user journey for organic and paid digital media, an Inbound Marketing offer that turned anonymous visitors into known prospective students, and a HubSpot drip campaign of emails and landing pages that nurtured them to application based on the actions they took.",
        { emphasis: "The technology mattered. But the journey determined what the technology needed to do." },
      ],
    },
    results: {
      heading: "Results",
      kind: "metrics",
      items: [
        {
          value: "44% ↑",
          label: "Year-over-year applications",
          detail: "Increase in applications for that fiscal year.",
        },
        {
          value: "$250K",
          label: "Saved annually",
          detail: "By eliminating technical debt and simplifying the systems behind the journey.",
        },
      ],
    },
    features: {
      heading: "What We Built",
      lead: {
        eyebrow: "Inbound Marketing",
        visual: "exchange",
        title: "Give something. Get a name.",
        body: [
          "A dedicated space in the journey where prospective students could share their name and get something genuinely useful in return.",
          "It turned anonymous attention into a relationship we could nurture. It’s a strategy I still use at Lawrence today.",
        ],
      },
      items: [
        {
          title: "One Journey for Organic and Paid Media",
          body: "A user journey designed across organic and paid digital media and the website, so every campaign led somewhere purposeful. HubSpot tracking on the website showed which pages each prospective student visited along the way.",
        },
        {
          title: "A HubSpot Drip Campaign to Application",
          body: "Emails and landing pages in HubSpot nurtured prospective students to application based on the actions they took, so what each student received next depended on what they’d already done.",
        },
      ],
    },
    process: {
      steps: [
        {
          title: "Attract",
          body: "Build campaigns and landing experiences around specific audiences, questions and actions.",
        },
        {
          title: "Identify",
          body: "Create meaningful opportunities for anonymous visitors to become known prospective students.",
        },
        {
          title: "Understand",
          body: "Use behavioral data to understand what people continue exploring and what that behavior may signal.",
        },
        {
          title: "Act",
          body: "Turn digital behavior into audiences and insight Admissions can actually use.",
        },
      ],
      flow: ["Attract", "Identify", "Understand", "Nurture", "Prioritize", "Hand off"],
    },
    decisions: [
      {
        title: "Get the Name. Then Understand the Journey.",
        lede: "A conversion wasn’t the finish line.",
        body: [
          "Once a prospective student identified themselves, we could begin understanding what they explored next, what questions they appeared to be answering and whether their behavior suggested increasing interest.",
        ],
        closing: "That changed the goal from simply generating leads to understanding intent.",
      },
      {
        title: "Treat Marketing and Admissions as One Journey",
        lede: "Prospective students don’t care which department owns a platform.",
        body: [
          "Marketing could build awareness and identify behavior. Admissions could turn that interest into a relationship. The digital journey needed to support movement between the two rather than reinforce the organizational boundary.",
        ],
      },
      {
        title: "Know When to Remove Technology",
        body: [
          "We worked through an API connection between HubSpot and Slate. When parts of that integration became unnecessary technical debt and existing admissions forms could accomplish the job more directly, we simplified.",
          "Eliminating that technical debt saved $250K annually.",
        ],
        closing: "The best technical solution isn’t always another integration. Sometimes it’s removing one.",
      },
    ],
    learned: {
      headline: "This work changed how I think about marketing technology.",
      body: [
        "A CRM isn’t a customer journey. Automation isn’t a strategy. And collecting more data doesn’t automatically mean understanding people better.",
        "Technology is most useful when it disappears into a journey that makes sense.",
      ],
      closing: "Start with the human journey. Then decide what technology deserves to exist around it.",
    },
    deeper: {
      title: "Want to Go Deeper?",
      body: [
        "There’s more to this project than a public case study can responsibly show.",
        "I can walk through the customer journey, landing-page strategy, behavioral tracking, lead scoring, automation, CRM architecture and the decisions that shaped how Marketing and Admissions used the system together.",
        "I can also talk about what didn’t work, what we simplified and why.",
      ],
      cta: "Discuss this project",
    },
    relatedThoughts: ["progress-should-still-feel-human"],
  },

  "lawrence-responsible-ai": {
    number: "01",
    lede: "Building a human-centered framework for responsible AI exploration while the technology, and the questions surrounding it, are still evolving.",
    facts: [
      { label: "Role", value: "Co-chair, AI Governance Council" },
      { label: "Scope", value: "AI Governance · Responsible Technology · Institutional Strategy · Ethics" },
    ],
    challenge: {
      headline: "AI was already here. A shared philosophy wasn’t.",
      body: [
        "People across the institution were experimenting while questions about appropriate use, transparency, accessibility, sustainability and human impact were developing just as quickly as the technology itself.",
        "Simply blocking AI wasn’t realistic.",
        "Adopting it everywhere wasn’t responsible either.",
        "The harder question became:",
        { emphasis: "How does an institution make thoughtful decisions about technology that is changing faster than its policies can?" },
      ],
    },
    solution: {
      headline: "Build the ability to make better decisions.",
      body: [
        "I co-chair the institution-wide effort alongside the Associate Provost, bringing a digital strategy, implementation and human-centered technology perspective to the work.",
        "Rather than beginning with a long list of rules, we’re creating infrastructure for responsible exploration: governance, shared questions, institutional dialogue and a repeatable way to evaluate emerging uses.",
        "The goal isn’t to know every answer.",
        { emphasis: "It’s to get better at asking the questions that should come before implementation." },
      ],
    },
    results: {
      heading: "What Changed",
      kind: "shifts",
      items: [
        {
          value: "Siloed → Shared",
          label: "Create a common starting point",
          detail: "Move AI conversations out of isolated departments and toward an institution-wide governance structure.",
        },
        {
          value: "Tools → Questions",
          label: "Start before vendor selection",
          detail: "Shift the conversation from “Which AI should we use?” toward whether AI belongs in the solution at all.",
        },
        {
          value: "Policy → Governance",
          label: "Build something that can evolve",
          detail: "Create a decision-making structure capable of adapting rather than pretending today’s rules can anticipate tomorrow’s technology.",
        },
      ],
    },
    process: {
      layout: "timeline",
      title: "AI Valuation & Optimization",
      steps: [
        {
          title: "Identify & Validate",
          body: "Discover meaningful business problems and AI opportunities, then assess value potential, data suitability, risks, and organizational readiness.",
          questions: [
            "What specific business problem does it solve?",
            "Which measurable business metric should improve, and is suitable data available?",
            "What are the consequences if the AI system makes a mistake?",
            "Could a simpler or less expensive solution deliver most of the value?",
          ],
          example:
            "For example, rather than saying, “We need an AI chatbot,” the organization might say, “Our customer service team spends too much time answering repetitive questions. We want to reduce average response time while maintaining customer satisfaction.”",
        },
        {
          title: "Strategize",
          body: "Determine the right solution and plan how it will be delivered, measured and governed.",
        },
        {
          title: "Implement",
          body: "Launch a focused, controlled AI solution rather than attempting a massive rollout.",
        },
        {
          title: "Optimize",
          body: "Measure outcomes, learn from actual usage, and improve performance.",
        },
        {
          title: "Scale",
          body: "Expand only after demonstrating repeatable value and operational readiness.",
        },
      ],
    },
    decisions: [
      {
        title: "Don’t Make Adoption the Goal",
        lede: "The existence of useful AI tools doesn’t create an obligation to use them.",
        body: [
          "The approach needed room for experimentation, skepticism and non-use. Responsible implementation means asking whether AI belongs before deciding how to deploy it.",
        ],
      },
      {
        title: "Put Human Consequences Upstream",
        lede: "Technical capability is only one dimension of a responsible decision.",
        body: [
          "Who is affected? What should they know? Where should human judgment remain? What are the implications for accessibility, privacy, sustainability and meaningful choice?",
        ],
        closing: "Those questions belong before implementation, not after it.",
      },
      {
        title: "Govern for Change, Not Certainty",
        lede: "AI is evolving too quickly for a static rulebook to answer every future question.",
        body: [
          "A more durable approach is building the institutional capacity to evaluate new situations as they emerge.",
        ],
        closing: "The framework matters. The ability to keep questioning it matters more.",
      },
    ],
    learned: {
      headline: "I’m not particularly interested in the divide between “pro-AI” and “anti-AI.”",
      body: [
        "Both positions can make something extraordinarily complicated sound deceptively simple.",
        "I’m more interested in the gray: where technological possibility collides with human behavior, accessibility, sustainability, institutional responsibility and the fact that we don’t yet know all of the consequences.",
        "In a field changing this quickly, the ability to continually ask better questions may be more durable than rules written around today’s tools.",
      ],
      closing: "Progress should still feel human.",
    },
    deeper: {
      title: "Want to Talk About the Gray?",
      body: [
        "Some of the most meaningful parts of governance work happen in conversations, working frameworks and institutional decision-making that don’t belong on a public portfolio.",
        "I can talk more about the governance model, framework development, implementation questions and how I think organizations can explore AI without treating adoption as the inevitable outcome.",
      ],
      cta: "Discuss this project",
    },
    relatedThoughts: ["progress-should-still-feel-human"],
  },
};
